import {
	observable,
	autorun,
	computed,
	action,
	runInAction,
	reaction,
} from "mobx";
import { HubConnectionFactory, IConnection, HubState } from "@gp/hub";

import { getCurrentLanguage } from "@utils";
import { analyticsTracker } from "@utils/analytics";

import { StorageStateKeysEnum } from "@utils/storage";
import type { RootStore } from "@state/stores/types";
import { UserHub } from "./user-hub/UserHub";
import {
	UserHubOptions,
	USER_HUB_SUBSCRIPTION_ID,
} from "./user-hub/UserHubOptions";
import {
	UserNotificationData,
	UserNotificationEnvelope,
	UserHubNotificationResponse,
} from "@api-types/user-hub/UserHubTypes";
import { Subscription } from "rxjs";

export class UserHubStore {
	@observable public hub: UserHub;
	@observable public isSessionExpired: boolean = false;
	private connection: IConnection;
	private rootStore: RootStore;
	private _disconnectDebounce: ReturnType<typeof setTimeout> | undefined;
	private subscription: Subscription | null = null;
	private autorunDisposer: (() => void) | null = null;
	private reactionDisposer: (() => void) | null = null;

	@computed private get connectionFactory() {
		const token =
			this.rootStore.userAuthStore.onlineUserToken ??
			this.rootStore.userAuthStore.shopUserToken;
		if (!token) {
			return null;
		}

		const query = new URLSearchParams();
		query.append("token", token);
		query.append("language", getCurrentLanguage());

		const connectionFactory = new HubConnectionFactory(
			{ get: () => query.toString() },
			{
				serverUrl:
					WEBPACK_NOTIFICATION_API_ADDRESS + "/notification-proxy",
				enableLogging: false,
			}
		);

		return connectionFactory;
	}

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	@action.bound
	public async initializeUserHub() {
		if (!WEBPACK_IS_NOTIFICATION_HUB_ENABLED) {
			return;
		}

		if (
			!this.rootStore.userAuthStore.user &&
			!this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.USER_KEY
			)
		) {
			return;
		}

		this.autorunDisposer?.();
		this.reactionDisposer?.();
		clearTimeout(this._disconnectDebounce);
		this.subscription?.unsubscribe();
		this.subscription = null;
		this.connection?.disconnect();

		this.autorunDisposer = autorun(() => {
			if (this.hub == null || this.isSessionExpired) {
				return;
			}
			if (this.rootStore.userAuthStore.user == null) {
				return;
			}

			if (this.hub.dispatchers.size === 0) {
				this.destroy();
				document.removeEventListener("click", this.clickHandler);
				return;
			}

			document.addEventListener("click", this.clickHandler);
			this.startSession();
		});

		this.reactionDisposer = reaction(
			() => this.connectionFactory,
			(connectionFactory) => {
				const prevHubState =
					this.connection?.state || HubState.NOT_INITIALIZED;
				// Disconnect prev connection and clean up old subscription
				this.connection?.disconnect();
				this.subscription?.unsubscribe();
				this.subscription = null;
				if (!this.rootStore.userAuthStore.user) {
					return null;
				}
				if (!connectionFactory) {
					return null;
				}
				this.connection = connectionFactory.createConnection();

				this.hub?.initialize(this.connection);

				switch (prevHubState) {
					case HubState.CONNECTING:
					case HubState.CONNECTED:
					case HubState.AUTO_RECONNECT:
					case HubState.RECONNECTING:
					case HubState.ERROR:
					case HubState.RECONNECTING_AFTER_ERROR:
						if (this.hub.dispatchers.size > 0) {
							this.startSession();
							this.subscribeToNotifications();
						}
					default:
					// Empty
				}
			}
		);

		const factory = this.connectionFactory;
		if (!factory) {
			return;
		}

		const options = new UserHubOptions();
		this.connection = factory.createConnection();
		this.hub = new UserHub(options, this.connection);
		this.connection.connect();

		this.subscribeToNotifications();
	}

	@action.bound
	private subscribeToNotifications() {
		const token =
			this.rootStore.userAuthStore.onlineUserToken ??
			this.rootStore.userAuthStore.shopUserToken;
		if (!token) {
			return;
		}

		const subscriptionObservable = this.hub.getSubscription({
			subscriptionId: USER_HUB_SUBSCRIPTION_ID,
			token: token,
		});

		this.subscription = subscriptionObservable.subscribe({
			next: (envelopes: UserNotificationEnvelope[]) => {
				runInAction(() => {
					for (const envelope of envelopes) {
						const raw = envelope.data || (envelope as any).Data;
						const data = this.normalizeNotificationData(raw);
						if (data) {
							this.handleNotificationByType(data);
						}
					}
				});
			},
			error: (err) => {
				console.error("UserHub subscription error:", err);
			},
		});
	}

	private normalizeNotificationData(raw: any): UserNotificationData | null {
		if (!raw) {
			return null;
		}
		return {
			id: raw.id || raw.Id,
			timeStamp: raw.timeStamp || raw.TimeStamp,
			type: raw.type || raw.Type,
			userId: raw.userId || raw.UserId,
			payload: raw.payload || raw.Payload,
		};
	}

	private handleNotificationByType(notification: UserNotificationData) {
		switch (notification.type) {
			case "reality-check":
				this.rootStore.realityCheckStore.onHubRealityCheck(
					notification
				);
				break;
			case "first-deposit":
				analyticsTracker.trackFirstDeposit();
				break;
			default:
				break;
		}
	}

	generateUUID(): string {
		const bytes = crypto.getRandomValues(new Uint8Array(16));
		bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
		bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
		const hex = [...bytes]
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("");
		return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
	}

	@action.bound
	public async acknowledgeNotification(notification: UserNotificationData) {
		const userId = this.rootStore.userAuthStore.getUserId;
		if (!userId) {
			return;
		}

		const response: UserHubNotificationResponse = {
			Id: this.generateUUID(),
			TimeStamp: new Date().toISOString(),
			Type: "message-shown",
			UserId: userId,
			Payload: {
				originalNotificationId: notification.id,
				originalType: notification.type,
				shownAt: new Date().toISOString(),
			},
		};

		try {
			await this.hub.sendNotificationResponse(response);
		} catch (err) {
			console.error("Failed to acknowledge notification:", err);
		}
	}

	@action.bound
	public restartSession() {
		this.isSessionExpired = false;
	}

	@action.bound
	private startSession() {
		if (this.isSessionExpired) {
			// Requires manual session restart.
			return;
		}

		this.restartSession();
		clearTimeout(this._disconnectDebounce);
		this._disconnectDebounce = undefined;

		this.connection.connect();

		this._disconnectDebounce = setTimeout(() => {
			runInAction(() => {
				this.connection?.disconnect();
				if (this.hub.dispatchers.size > 0) {
					this.isSessionExpired = true;
				}
			});
		}, 600_000);
	}

	@action.bound
	private destroy() {
		this.subscription?.unsubscribe();
		this.subscription = null;
		clearTimeout(this._disconnectDebounce);
		this._disconnectDebounce = setTimeout(() => {
			runInAction(() => {
				this.connection?.disconnect();
				this.isSessionExpired = false;
			});
		}, 600_000);
	}

	@action.bound
	public destroyConnectionOnUserLogout() {
		this.autorunDisposer?.();
		this.autorunDisposer = null;
		this.reactionDisposer?.();
		this.reactionDisposer = null;
		clearTimeout(this._disconnectDebounce);
		this.subscription?.unsubscribe();
		this.subscription = null;
		this.connection?.disconnect();
		this.isSessionExpired = false;
	}

	@action.bound
	private clickHandler = () => {
		if (this.hub?.dispatchers.size > 0) {
			this.startSession();
		}
	};
}
